import { Col, Form, Row } from 'antd';
import { cloneDeep } from 'es-toolkit';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ReactComponent as FilterIcon } from '../../../../../../assets/icons/filter.svg';
import { filterRateShopAdditionalFieldsVersionTwo } from '../../../../../../utilz';
import { routeFileSelector } from '../../../../../../utilz/i18n';
import Button from '../../../../../components/Buttons/Button';
import IconButton from '../../../../../components/Buttons/IconButton';
import FilterCard from '../../../../../components/Cards/FilterCard';
import SvgConverter from '../../../../../components/Common/SvgConverter';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import ScrollPagination from '../../../../../components/Paginations/ScrollPagination';
import AccessorialCard from './AccessorialCard';
import AccessorialsFilterPopOver from './AccessorialsFilterPopOver';

const pageSize = 50;
const AccessorialsDetails = ({
  form,
  accessorials = [],
  customHeight = 'vh-30',
  setActionButtonDisabled = () => {},
  hasEditing = false,
  selectedAccessorialsRef,
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  let scrollRef = useRef(null);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  useEffect(() => {
    if (form && accessorials && !hasEditing) {
      let accessorialsLocal = cloneDeep(accessorials);
      accessorialsLocal = accessorialsLocal?.splice(0, pageSize);
      accessorialsLocal = accessorialsLocal.map((row) => ({
        ...row,
        show: true,
        isChecked:
          row.accChargeType === 'FUE'
            ? true
            : row.isChecked
              ? true
              : Boolean(selectedAccessorialsRef.current?.[row.accChargeType]),
      }));

      const filteredAccessorials = filterRateShopAdditionalFieldsVersionTwo(accessorialsLocal, {}, 'codeDesc');
      setAccessorials(filteredAccessorials);
      setDefaultItems(accessorials);
      setPageNumber(1);
      if (selectedAccessorialsRef.current) {
        for (const accType in selectedAccessorialsRef.current) {
          if (!accessorials?.find((item) => item.accChargeType === accType)) {
            delete selectedAccessorialsRef.current[accType];
          }
        }
      }
    }
  }, [accessorials, form]);

  const [defaultItems, setDefaultItems] = useState([]);

  const accessorialsFormData = Form.useWatch('accessorials', form);

  const handleDataChange = (data, index) => {
    const newData = form.getFieldValue('accessorials');
    newData[index] = { ...newData[index], ...data, isNew: false };
    setAccessorials(newData);
    setDefaultItems(newData);
    setActionButtonDisabled(false);
  };

  const handleRemove = () => {
    const newData = form.getFieldValue('accessorials');
    setDefaultItems(newData);
    setActionButtonDisabled(false);
  };

  const handelPagination = async () => {
    try {
      const accessorialsList = cloneDeep(accessorials);
      let accessorialsLocal = form.getFieldValue('accessorials');
      const currentPageIndex = pageNumber * pageSize;
      const nextItems = accessorialsList?.splice(currentPageIndex, pageSize);
      accessorialsLocal = [...accessorialsLocal, ...(nextItems ? nextItems : [])];
      accessorialsLocal = accessorialsLocal?.map((row) => ({
        ...row,
        show: true,
      }));

      setAccessorials(accessorialsLocal);
      setPageNumber((val) => val + 1);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleFilter = (filterData) => {
    if (filterData?.searchValue?.length > 0) {
      setIsFilterApplied(true);
    } else {
      setIsFilterApplied(false);
    }

    const selectedList = accessorialsFormData?.filter((item) => item.isChecked);
    const allAccessorialsLocal = cloneDeep(accessorials);

    selectedList.forEach((row) => {
      allAccessorialsLocal[allAccessorialsLocal?.findIndex((item) => item.lookupKey === row?.lookupKey)] = row;
    });

    let accessorialsListLocal = filterRateShopAdditionalFieldsVersionTwo(allAccessorialsLocal, filterData, 'codeDesc');

    if (!filterData?.searchValue || filterData?.searchValue?.length === 0) {
      accessorialsListLocal = accessorialsListLocal.splice(0, pageSize);
    }

    setAccessorials(accessorialsListLocal);
    setFilterOpen(false);
    setPageNumber(1);

    if (scrollRef?.current) {
      scrollRef.current.scrollTo(0, 0);
    }
  };

  const handlePaginationRef = (ref) => {
    scrollRef = ref;
  };

  const setAccessorials = (list) => {
    form.setFieldValue('accessorials', list);
  };

  const onCheckboxChange = (accType) => (e) => {
    if (selectedAccessorialsRef.current) selectedAccessorialsRef.current[accType] = e.target.checked;
  };

  return (
    <Form.List name="accessorials">
      {(accessorialsField, { add, move, remove }) => (
        <FilterCard
          className={`${customHeight} overflow-hidden d-flex flex-direction-column`}
          shadow={false}
          showBorder
          headerClassName="background-selected"
          customHeader={
            <div className="w-100">
              <Row justify="space-between" align="middle">
                <Col>
                  <PrimaryLabel className="text-black">
                    {t('translation:shipmentAdd.accountingDetails.accessorialDetails')}
                  </PrimaryLabel>
                </Col>
                <Col>
                  <Row gutter={15} align="middle">
                    <Col>
                      <Button
                        onClick={() => {
                          add({
                            editMode: true,
                            isNew: true,
                            codeId: 0,
                            showActions: true,
                          });
                          move(accessorialsField.length, 0);
                        }}
                      >
                        {t('translation:getQuote.additionalDetails.addNewAccessorialCharges')}
                      </Button>
                    </Col>
                    <Col>
                      <AccessorialsFilterPopOver open={filterOpen} setOpen={setFilterOpen} handleFilter={handleFilter}>
                        <IconButton onClick={() => setFilterOpen(true)}>
                          <SvgConverter Icon={FilterIcon} color="black" />
                        </IconButton>
                      </AccessorialsFilterPopOver>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          }
        >
          <div className="flex-1 overflow-hidden d-flex flex-direction-column">
            <ScrollPagination
              getByRef={handlePaginationRef}
              className="flex-1"
              isLoading={loading}
              hasMore={accessorialsFormData?.length < accessorials?.length && !isFilterApplied}
              loadMore={() => {
                setLoading(true);
                handelPagination();
              }}
              scrollOffset={30}
            >
              {accessorialsField.map(({ name }) => (
                <AccessorialCard
                  key={accessorialsFormData?.[name]?.accChargeType}
                  name={name}
                  form={form}
                  accessorialDetails={accessorialsFormData?.[name]}
                  handleDataChange={handleDataChange}
                  defaultValues={defaultItems?.[name]}
                  handleRemove={(nameValue) => {
                    remove(nameValue);
                    handleRemove();
                  }}
                  onCheckboxChange={onCheckboxChange}
                />
              ))}
            </ScrollPagination>
          </div>
        </FilterCard>
      )}
    </Form.List>
  );
};

export default memo(AccessorialsDetails);
